import React, { useState, useEffect, useCallback, useRef } from "react";
import { Row, Col, Button, Card, Spinner } from "react-bootstrap";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  Formik,
  Form as FormikForm,
  ErrorMessage,
  useFormikContext,
} from "formik";
import * as Yup from "yup";
import { ChevronDown, FileText, Briefcase, Users } from "lucide-react";
import { CKEditor } from "@ckeditor/ckeditor5-react";
import ClassicEditor from "@ckeditor/ckeditor5-build-classic";

import TextInput from "../../../common/FormFields/TextInput";
import CustomAsyncDropdown from "../../../common/CustomAsyncDropdown";

import { getContacts } from "../../../../../services/contactServices.js";
import { getBatches } from "../../../../../services/BatchService";
import {
  getDepartmentsV2,
  getcourses,
  getPlacementOfficers,
} from "../../../../../services/CourseModule";
import { getOrganizations } from "../../../../../services/organizationServices.js";
import {
  getCourseBatchStudents,
  updatePlacement,
} from "../../../../../services/placementServices.js";

const PAGE_LIMIT = 20;

// ─── ScrollToFieldError ───────────────────────────────────────────────────────
const ScrollToFieldError = ({ setExpandedAccordion }) => {
  const { submitCount, isValid, errors } = useFormikContext();

  useEffect(() => {
    if (isValid || submitCount === 0) return;

    const fieldErrorNames = Object.keys(errors);
    if (fieldErrorNames.length <= 0) return;

    const firstError = fieldErrorNames[0];

    // Determine which section the error is in
    let sectionToOpen = 'jobDetails';
    if (['selectedBatches', 'selectedCourses', 'coursewiseStudents', 'batchwiseStudents'].includes(firstError)) {
      sectionToOpen = 'studentSelectionDetails';
    } else if (['jobDescription'].includes(firstError)) {
      sectionToOpen = 'jobDescriptionDetails';
    }

    if (setExpandedAccordion) {
       setExpandedAccordion(prev => ({...prev, [sectionToOpen]: true}));
    }

    setTimeout(() => {
      const element =
        document.querySelector(`input[name='${firstError}']`) ||
        document.querySelector(`textarea[name='${firstError}']`) ||
        document.querySelector(`[data-field='${firstError}']`) ||
        document.querySelector(`select[name='${firstError}']`);

      if (element) {
        element.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    }, 150);
  }, [submitCount, isValid, errors, setExpandedAccordion]);

  return null;
};

// ─── Main Component ───────────────────────────────────────────────────────────
const PlacementDetailsTab = ({ placement, onHide, onUpdatePlacement }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [expandedAccordion, setExpandedAccordion] = useState({
    jobDetails: true,
    studentSelectionDetails: false,
    jobDescriptionDetails: false,
  });

  // Refs to prevent multiple calls and track component mount state
  const initialLoadDone = useRef(false);
  const menuOpenInProgress = useRef(false);
  const cwStudentFetchInProgress = useRef(false);
  const bwStudentFetchInProgress = useRef(false);
  const isSubmitting = useRef(false);
  const isComponentMounted = useRef(true);
  const abortControllers = useRef({
    placementOfficers: null,
    contacts: null,
    orgs: null,
    depts: null,
    courses: null,
    batches: null,
    cwStudents: null,
    bwStudents: null,
  });

  // ── Hardcoded options ──────────────────────────────────────────────────────
  const hardcodedOptions = {
    status: [
      { value: "active", label: "Active" },
      { value: "inactive", label: "Inactive" },
    ],
    jobPositionStatus: [
      { value: "open", label: "Open" },
      { value: "in-progress", label: "In Progress" },
      { value: "interview-scheduled", label: "Interview Scheduled" },
      { value: "offer-made", label: "Offer Made" },
      { value: "hired", label: "Hired" },
      { value: "rejected", label: "Rejected" },
      { value: "closed", label: "Closed" },
      { value: "on-hold", label: "On Hold" },
    ],
  };

  // ── Dropdown state ─────────────────────────────────────────────────────────
  const [placementOfficersOptions, setPlacementOfficersOptions] = useState([]);
  const [placementOfficersPage, setPlacementOfficersPage] = useState(1);
  const [placementOfficersHasMore, setPlacementOfficersHasMore] = useState(true);
  const [placementOfficersLoading, setPlacementOfficersLoading] = useState(false);
  const [placementOfficersSearch, setPlacementOfficersSearch] = useState("");

  const [contactsOptions, setContactsOptions] = useState([]);
  const [contactsPage, setContactsPage] = useState(1);
  const [contactsHasMore, setContactsHasMore] = useState(true);
  const [contactsLoading, setContactsLoading] = useState(false);
  const [contactsSearch, setContactsSearch] = useState("");

  const [orgsOptions, setOrgsOptions] = useState([]);
  const [orgsPage, setOrgsPage] = useState(1);
  const [orgsHasMore, setOrgsHasMore] = useState(true);
  const [orgsLoading, setOrgsLoading] = useState(false);
  const [orgsSearch, setOrgsSearch] = useState("");

  const [deptsOptions, setDeptsOptions] = useState([]);
  const [deptsPage, setDeptsPage] = useState(1);
  const [deptsHasMore, setDeptsHasMore] = useState(true);
  const [deptsLoading, setDeptsLoading] = useState(false);
  const [deptsSearch, setDeptsSearch] = useState("");

  const [coursesOptions, setCoursesOptions] = useState([]);
  const [coursesPage, setCoursesPage] = useState(1);
  const [coursesHasMore, setCoursesHasMore] = useState(true);
  const [coursesLoading, setCoursesLoading] = useState(false);
  const [coursesSearch, setCoursesSearch] = useState("");

  const [batchesOptions, setBatchesOptions] = useState([]);
  const [batchesPage, setBatchesPage] = useState(1);
  const [batchesHasMore, setBatchesHasMore] = useState(true);
  const [batchesLoading, setBatchesLoading] = useState(false);
  const [batchesSearch, setBatchesSearch] = useState("");

  const [cwStudentsOptions, setCwStudentsOptions] = useState([]);
  const [cwStudentsLoading, setCwStudentsLoading] = useState(false);

  const [bwStudentsOptions, setBwStudentsOptions] = useState([]);
  const [bwStudentsLoading, setBwStudentsLoading] = useState(false);

  // ── CKEditor z-index fix ───────────────────────────────────────────────────
  useEffect(() => {
    const style = document.createElement("style");
    style.textContent = `
      .ck-editor__main { min-height: 300px; }
      .ck-editor__editable { min-height: 250px; }
      .ck-toolbar {
        display: flex !important; visibility: visible !important; opacity: 1 !important;
        z-index: 10060 !important; border-bottom: 1px solid #c4c4c4 !important;
        background: #f8f9fa !important;
      }
      .ck-balloon-panel, .ck.ck-balloon-panel, .ck-dropdown__panel, .ck.ck-dropdown__panel,
      .ck-toolbar__items, .ck.ck-toolbar__items, .ck-list, .ck.ck-list,
      .ck-button__label, .ck.ck-button__label { z-index: 10060 !important; }
      .ck-dropdown__panel { position: absolute !important; visibility: visible !important; opacity: 1 !important; display: block !important; }
      .ck.ck-toolbar { opacity: 1 !important; visibility: visible !important; display: flex !important; flex-wrap: wrap !important; }
      .ck.ck-toolbar .ck-toolbar__items { display: flex !important; flex-wrap: wrap !important; opacity: 1 !important; visibility: visible !important; }
    `;
    document.head.appendChild(style);
    return () => {
      if (document.head.contains(style)) document.head.removeChild(style);
    };
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    isComponentMounted.current = true;
    return () => {
      isComponentMounted.current = false;
      // Abort all pending requests
      Object.keys(abortControllers.current).forEach(key => {
        if (abortControllers.current[key]) {
          abortControllers.current[key].abort();
          abortControllers.current[key] = null;
        }
      });
    };
  }, []);

  // ══════════════════════════════════════════════════════════════════════════
  //  HELPERS
  // ══════════════════════════════════════════════════════════════════════════

  /** Extract a plain id string from a populated array OR a plain string */
  const resolveId = (field) => {
    if (!field) return "";
    if (Array.isArray(field) && field.length > 0)
      return field[0]?.id || field[0]?._id || String(field[0]) || "";
    if (typeof field === "object") return field.id || field._id || "";
    return String(field);
  };

  /** Build react-select options from a populated [{id, name}] array */
  const buildOptions = (field, labelFn = (item) => item.name || item.id || item._id) => {
    if (!Array.isArray(field) || !field.length || typeof field[0] !== "object")
      return [];
    return field.map((item) => ({ value: item.id || item._id, label: labelFn(item) }));
  };

  /**
   * Resolve the display value for a single-select dropdown.
   */
  const resolveOption = (options, value, fallbackFn) => {
    if (!value) return null;
    return options.find((o) => o.value === value) || fallbackFn();
  };

  // Fallback option builders
  const fallbackPlacementOfficerOption = useCallback((id) => {
    if (!id) return null;
    const arr = Array.isArray(placement?.assignedToId)
      ? placement.assignedToId
      : [];
    const found = arr.find((o) => o.id === id);
    if (found) {
      return {
        value: found.id,
        label:
          [found.firstName, found.lastName].filter(Boolean).join(" ") || id,
      };
    }
    return { value: id, label: id };
  }, [placement?.assignedToId]);

  const fallbackContactOption = useCallback((id) => {
    if (!id) return null;
    const arr = Array.isArray(placement?.contactId) ? placement.contactId : [];
    const found = arr.find((o) => o.id === id);
    return found
      ? { value: found.id, label: found.name || id }
      : { value: id, label: id };
  }, [placement?.contactId]);

  const fallbackOrgOption = useCallback((id, name) => {
    if (!id) return null;
    return {
      value: id,
      label: name || id,
      organizationId: id,
      organizationName: name || id,
    };
  }, []);

  const fallbackDeptOption = useCallback((id) => {
    if (!id) return null;
    const arr = Array.isArray(placement?.department)
      ? placement.department
      : [];
    const found = arr.find((o) => o.id === id);
    return found
      ? { value: found.id, label: found.name || id }
      : { value: id, label: id };
  }, [placement?.department]);

  // ── Derive initial form values
  const getInitialValues = () => {
    if (!placement) {
      return {
        jobName: "",
        organizationName: "",
        organizationId: "",
        jobPostedDate: "",
        salary: "",
        jobLocation: "",
        status: "",
        assignedToId: "",
        department: "",
        contactId: "",
        jobPositionStatus: "",
        jobDescription: "",
        selectedBatches: [],
        selectedCourses: [],
        coursewiseStudents: [],
        batchwiseStudents: [],
      };
    }

    const orgArr = Array.isArray(placement.organizationId)
      ? placement.organizationId
      : [];
    const orgId =
      orgArr[0]?.id ||
      orgArr[0]?._id ||
      (typeof placement.organizationId === "string"
        ? placement.organizationId
        : "");
    const orgName = orgArr[0]?.name || placement.organizationName || "";

    let assignedToId = "";
    if (
      Array.isArray(placement.assignedToId) &&
      placement.assignedToId.length > 0
    ) {
      assignedToId = placement.assignedToId[0]?.id || "";
    } else if (typeof placement.assignedToId === "string") {
      assignedToId = placement.assignedToId;
    }

    return {
      jobName: placement.jobName || "",
      organizationName: orgName,
      organizationId: orgId,
      jobPostedDate: placement.jobPostedDate
        ? placement.jobPostedDate.split("T")[0]
        : "",
      salary: placement.salary || placement.package || "",
      jobLocation: placement.jobLocation || "",
      status: placement.status || "",
      assignedToId,
      department: resolveId(placement.department),
      contactId: resolveId(placement.contactId),
      jobPositionStatus: placement.jobPositionStatus || "",
      jobDescription: placement.jobDescription || "",
      selectedBatches: buildOptions(placement.batchIds),
      selectedCourses: buildOptions(placement.courseModuleIds),
      coursewiseStudents: buildOptions(placement.coursewiseStudentIds),
      batchwiseStudents: buildOptions(placement.batchwiseStudentIds),
    };
  };

  // ══════════════════════════════════════════════════════════════════════════
  //  FETCH FUNCTIONS (wrapped with useCallback)
  // ══════════════════════════════════════════════════════════════════════════

  // ── Placement Officers ─────────────────────────────────────────────────────
  const fetchPlacementOfficers = useCallback(async ({
    page = 1,
    search = "",
    append = false,
  } = {}) => {
    if (placementOfficersLoading || !isComponentMounted.current) return;
    
    // Cancel previous request
    if (abortControllers.current.placementOfficers) {
      abortControllers.current.placementOfficers.abort();
    }
    
    const abortController = new AbortController();
    abortControllers.current.placementOfficers = abortController;
    
    setPlacementOfficersLoading(true);
    try {
      const response = await getPlacementOfficers(PAGE_LIMIT, page, search, { signal: abortController.signal });
      if (!isComponentMounted.current) return;
      const mapped = (response?.data || []).map((o) => ({
        value: o.id,
        label: `${o.firstName} ${o.lastName} | ${o.emailId}`,
      }));
      setPlacementOfficersOptions((prev) =>
        append ? [...prev, ...mapped] : mapped,
      );
      setPlacementOfficersPage(page);
      setPlacementOfficersHasMore(
        page < (response?.pagination?.totalPages || 1),
      );
    } catch (e) {
      if (e.name !== 'AbortError' && isComponentMounted.current) {
        console.error("Failed to load placement officers:", e);
      }
    } finally {
      if (isComponentMounted.current) {
        setPlacementOfficersLoading(false);
      }
      if (abortControllers.current.placementOfficers === abortController) {
        abortControllers.current.placementOfficers = null;
      }
    }
  }, [placementOfficersLoading]);

  // ── Contacts ───────────────────────────────────────────────────────────────
  const fetchContacts = useCallback(async ({
    page = 1,
    search = "",
    append = false,
  } = {}) => {
    if (contactsLoading || !isComponentMounted.current) return;
    
    if (abortControllers.current.contacts) {
      abortControllers.current.contacts.abort();
    }
    
    const abortController = new AbortController();
    abortControllers.current.contacts = abortController;
    
    setContactsLoading(true);
    try {
      const response = await getContacts(
        PAGE_LIMIT,
        page,
        search ? { search } : {},
        { signal: abortController.signal }
      );
      if (!isComponentMounted.current) return;
      const mapped = (response?.data || []).map((c) => ({
        value: c.id || c._id,
        label: `${c.name} | ${c.email}`,
        organizationId: Array.isArray(c.organizationId) 
          ? (c.organizationId[0]?.id || c.organizationId[0]?._id)
          : (c.organizationId?.id || c.organizationId?._id || c.organizationId || ""),
        organizationName: Array.isArray(c.organizationId)
          ? (c.organizationId[0]?.name || "")
          : (c.organizationId?.name || c.organizationName || ""),
      }));
      setContactsOptions((prev) => (append ? [...prev, ...mapped] : mapped));
      setContactsPage(page);
      setContactsHasMore(mapped.length === PAGE_LIMIT);
    } catch (e) {
      if (e.name !== 'AbortError' && isComponentMounted.current) {
        console.error("Failed to load contacts:", e);
      }
    } finally {
      if (isComponentMounted.current) setContactsLoading(false);
      if (abortControllers.current.contacts === abortController) {
        abortControllers.current.contacts = null;
      }
    }
  }, [contactsLoading]);

  // ── Organizations ──────────────────────────────────────────────────────────
  const fetchOrgs = useCallback(async ({ page = 1, search = "", append = false } = {}) => {
    if (orgsLoading || !isComponentMounted.current) return;
    
    if (abortControllers.current.orgs) {
      abortControllers.current.orgs.abort();
    }
    
    const abortController = new AbortController();
    abortControllers.current.orgs = abortController;
    
    setOrgsLoading(true);
    try {
      const response = await getOrganizations(
        PAGE_LIMIT,
        page,
        search ? { search } : {},
        { signal: abortController.signal }
      );
      if (!isComponentMounted.current) return;
      const rawData = response?.data?.data || response?.data || [];
      const mapped = rawData.map((org) => ({
        value: org.id || org._id,
        label: org.name || "Unnamed Organization",
        organizationId: org.id || org._id,
        organizationName: org.name || "Unnamed Organization",
      }));
      setOrgsOptions((prev) => (append ? [...prev, ...mapped] : mapped));
      setOrgsPage(page);
      setOrgsHasMore(mapped.length === PAGE_LIMIT);
    } catch (e) {
      if (e.name !== 'AbortError' && isComponentMounted.current) {
        console.error("Failed to load organizations:", e);
      }
    } finally {
      if (isComponentMounted.current) setOrgsLoading(false);
      if (abortControllers.current.orgs === abortController) {
        abortControllers.current.orgs = null;
      }
    }
  }, [orgsLoading]);

  // ── Departments ────────────────────────────────────────────────────────────
  const fetchDepts = useCallback(async ({ page = 1, search = "", append = false } = {}) => {
    if (deptsLoading || !isComponentMounted.current) return;
    
    if (abortControllers.current.depts) {
      abortControllers.current.depts.abort();
    }
    
    const abortController = new AbortController();
    abortControllers.current.depts = abortController;
    
    setDeptsLoading(true);
    try {
      const response = await getDepartmentsV2(PAGE_LIMIT, page, search, { signal: abortController.signal });
      if (!isComponentMounted.current) return;
      const mapped = (response?.data || []).map((d) => ({
        value: d.id,
        label: d.name,
      }));
      setDeptsOptions((prev) => (append ? [...prev, ...mapped] : mapped));
      setDeptsPage(page);
      const pagination = response?.pagination;
      setDeptsHasMore(
        pagination
          ? page < pagination.totalPages
          : mapped.length === PAGE_LIMIT,
      );
    } catch (e) {
      if (e.name !== 'AbortError' && isComponentMounted.current) {
        console.error("Failed to load departments:", e);
      }
    } finally {
      if (isComponentMounted.current) setDeptsLoading(false);
      if (abortControllers.current.depts === abortController) {
        abortControllers.current.depts = null;
      }
    }
  }, [deptsLoading]);

  // ── Courses ───────────────────────────────────────────────────────────────
  const fetchCourses = useCallback(async ({
    page = 1,
    search = "",
    append = false,
  } = {}) => {
    if (coursesLoading || !isComponentMounted.current) return;
    
    if (abortControllers.current.courses) {
      abortControllers.current.courses.abort();
    }
    
    const abortController = new AbortController();
    abortControllers.current.courses = abortController;
    
    setCoursesLoading(true);
    try {
      const response = await getcourses(PAGE_LIMIT, page, search, { signal: abortController.signal });
      if (!isComponentMounted.current) return;
      const mapped = (response?.data || []).map((c) => ({
        value: c.id,
        label: c.name ? c.name.trim() : "Unnamed Course",
      }));
      setCoursesOptions((prev) => (append ? [...prev, ...mapped] : mapped));
      setCoursesPage(page);
      setCoursesHasMore(mapped.length === PAGE_LIMIT);
    } catch (e) {
      if (e.name !== 'AbortError' && isComponentMounted.current) {
        console.error("Failed to load courses:", e);
      }
    } finally {
      if (isComponentMounted.current) setCoursesLoading(false);
      if (abortControllers.current.courses === abortController) {
        abortControllers.current.courses = null;
      }
    }
  }, [coursesLoading]);

  // ── Batches ───────────────────────────────────────────────────────────────
  const fetchBatches = useCallback(async ({
    page = 1,
    search = "",
    append = false,
  } = {}) => {
    if (batchesLoading || !isComponentMounted.current) return;
    
    if (abortControllers.current.batches) {
      abortControllers.current.batches.abort();
    }
    
    const abortController = new AbortController();
    abortControllers.current.batches = abortController;
    
    setBatchesLoading(true);
    try {
      const response = await getBatches(PAGE_LIMIT, page, search, { signal: abortController.signal });
      if (!isComponentMounted.current) return;
      const mapped = (response?.data || []).map((b) => ({
        value: b.id,
        label: b.name,
      }));
      setBatchesOptions((prev) => (append ? [...prev, ...mapped] : mapped));
      setBatchesPage(page);
      setBatchesHasMore(mapped.length === PAGE_LIMIT);
    } catch (e) {
      if (e.name !== 'AbortError' && isComponentMounted.current) {
        console.error("Failed to load batches:", e);
      }
    } finally {
      if (isComponentMounted.current) setBatchesLoading(false);
      if (abortControllers.current.batches === abortController) {
        abortControllers.current.batches = null;
      }
    }
  }, [batchesLoading]);

  // ── Coursewise Students ────────────────────────────────────────────────────
  const fetchCwStudents = useCallback(async ({
    courseIds = [],
  } = {}) => {
    if (!courseIds.length || cwStudentsLoading || cwStudentFetchInProgress.current || !isComponentMounted.current) return;
    
    cwStudentFetchInProgress.current = true;
    
    if (abortControllers.current.cwStudents) {
      abortControllers.current.cwStudents.abort();
    }
    
    const abortController = new AbortController();
    abortControllers.current.cwStudents = abortController;
    
    setCwStudentsLoading(true);
    try {
      const response = await getCourseBatchStudents({
        courseIds,
        signal: abortController.signal
      });
      if (!isComponentMounted.current) return;
      const mapped = (response?.data || []).map((s) => ({
        value: s.id,
        label: s.email ? `${s.name} (${s.email})` : s.name,
      }));
      setCwStudentsOptions(mapped);
    } catch (e) {
      if (e.name !== 'AbortError' && isComponentMounted.current) {
        console.error("Failed to load coursewise students:", e);
      }
    } finally {
      if (isComponentMounted.current) setCwStudentsLoading(false);
      cwStudentFetchInProgress.current = false;
      if (abortControllers.current.cwStudents === abortController) {
        abortControllers.current.cwStudents = null;
      }
    }
  }, [cwStudentsLoading]);

  // ── Batchwise Students ─────────────────────────────────────────────────────
  const fetchBwStudents = useCallback(async ({
    batchIds = [],
  } = {}) => {
    if (!batchIds.length || bwStudentsLoading || bwStudentFetchInProgress.current || !isComponentMounted.current) return;
    
    bwStudentFetchInProgress.current = true;
    
    if (abortControllers.current.bwStudents) {
      abortControllers.current.bwStudents.abort();
    }
    
    const abortController = new AbortController();
    abortControllers.current.bwStudents = abortController;
    
    setBwStudentsLoading(true);
    try {
      const response = await getCourseBatchStudents({
        batchIds,
        signal: abortController.signal
      });
      if (!isComponentMounted.current) return;
      const mapped = (response?.data || []).map((s) => ({
        value: s.id,
        label: s.email ? `${s.name} (${s.email})` : s.name,
      }));
      setBwStudentsOptions(mapped);
    } catch (e) {
      if (e.name !== 'AbortError' && isComponentMounted.current) {
        console.error("Failed to load batchwise students:", e);
      }
    } finally {
      if (isComponentMounted.current) setBwStudentsLoading(false);
      bwStudentFetchInProgress.current = false;
      if (abortControllers.current.bwStudents === abortController) {
        abortControllers.current.bwStudents = null;
      }
    }
  }, [bwStudentsLoading]);

  // ══════════════════════════════════════════════════════════════════════════
  //  INITIAL LOAD - Only once when placement changes
  // ══════════════════════════════════════════════════════════════════════════
  useEffect(() => {
    if (!placement || !placement?.id) return;
    
    // Reset the flag when placement ID changes
    initialLoadDone.current = false;
    
    // Use setTimeout to ensure this runs after any parent re-renders
    const timer = setTimeout(() => {
      if (!initialLoadDone.current && placement?.id && isComponentMounted.current) {
        initialLoadDone.current = true;
        
        // Load all single-select dropdowns in parallel
        const loadInitialData = async () => {
          await Promise.all([
            fetchPlacementOfficers({ page: 1, search: "", append: false }),
            fetchContacts({ page: 1, search: "", append: false }),
            fetchOrgs({ page: 1, search: "", append: false }),
            fetchDepts({ page: 1, search: "", append: false }),
          ]);
        };
        
        loadInitialData();
        
        // Pre-load student options when existing selections are present
        const courseIds = buildOptions(placement.courseModuleIds).map((c) => c.value);
        if (courseIds.length > 0) {
          fetchCwStudents({ courseIds });
        }

        const batchIds = buildOptions(placement.batchIds).map((b) => b.value);
        if (batchIds.length > 0) {
          fetchBwStudents({ batchIds });
        }
      }
    }, 0);
    
    return () => clearTimeout(timer);
  }, [placement?.id]); // Only depend on placement.id

  // ══════════════════════════════════════════════════════════════════════════
  //  VALIDATION
  // ══════════════════════════════════════════════════════════════════════════
  const validationSchema = Yup.object({
    jobName: Yup.string().min(2).max(100).required("Job Name is required"),
    organizationName: Yup.string()
      .min(2)
      .max(100)
      .required("Organization Name is required"),
    organizationId: Yup.string(),
    jobPostedDate: Yup.date()
      .required("Job Posted Date is required")
      .typeError("Please enter a valid date"),
    salary: Yup.string()
      .matches(/^\d+$/, "Salary must be a number")
      .required("Salary is required"),
    jobLocation: Yup.string()
      .min(2)
      .max(100)
      .required("Job Location is required"),
    status: Yup.string().required("Status is required"),
    assignedToId: Yup.string().required("Assigned To is required"),
    department: Yup.string().required("Department is required"),
    contactId: Yup.string().required("Contact is required"),
    jobPositionStatus: Yup.string().required("Job Position Status is required"),
    jobDescription: Yup.string().nullable(),
    selectedBatches: Yup.array().optional(),
    selectedCourses: Yup.array().optional(),
    coursewiseStudents: Yup.array().optional(),
    batchwiseStudents: Yup.array().optional(),
  });

  // ══════════════════════════════════════════════════════════════════════════
  //  HANDLERS FOR DROPDOWNS
  // ══════════════════════════════════════════════════════════════════════════

  // Placement Officers Handlers
  const handlePlacementOfficersOpen = useCallback(() => {
    if (placementOfficersOptions.length === 0) {
      fetchPlacementOfficers({ page: 1, search: "", append: false });
    }
  }, [placementOfficersOptions.length, fetchPlacementOfficers]);

  const handlePlacementOfficersScrollBottom = useCallback(() => {
    if (!placementOfficersHasMore || placementOfficersLoading) return;
    fetchPlacementOfficers({
      page: placementOfficersPage + 1,
      search: placementOfficersSearch,
      append: true,
    });
  }, [placementOfficersHasMore, placementOfficersLoading, placementOfficersPage, placementOfficersSearch, fetchPlacementOfficers]);

  const handlePlacementOfficersSearch = useCallback((val) => {
    setPlacementOfficersSearch(val);
    setPlacementOfficersPage(1);
    setPlacementOfficersHasMore(true);
    fetchPlacementOfficers({ page: 1, search: val, append: false });
  }, [fetchPlacementOfficers]);

  const handlePlacementOfficersRefresh = useCallback(() => {
    setPlacementOfficersSearch("");
    setPlacementOfficersPage(1);
    setPlacementOfficersHasMore(true);
    fetchPlacementOfficers({ page: 1, search: "", append: false });
  }, [fetchPlacementOfficers]);

  // Contacts Handlers
  const handleContactsOpen = useCallback(() => {
    if (contactsOptions.length === 0)
      fetchContacts({ page: 1, search: "", append: false });
  }, [contactsOptions.length, fetchContacts]);

  const handleContactsScrollBottom = useCallback(() => {
    if (!contactsHasMore || contactsLoading) return;
    fetchContacts({
      page: contactsPage + 1,
      search: contactsSearch,
      append: true,
    });
  }, [contactsHasMore, contactsLoading, contactsPage, contactsSearch, fetchContacts]);

  const handleContactsSearch = useCallback((val) => {
    setContactsSearch(val);
    setContactsPage(1);
    setContactsHasMore(true);
    fetchContacts({ page: 1, search: val, append: false });
  }, [fetchContacts]);

  const handleContactsRefresh = useCallback(() => {
    setContactsSearch("");
    setContactsPage(1);
    setContactsHasMore(true);
    fetchContacts({ page: 1, search: "", append: false });
  }, [fetchContacts]);

  // Organizations Handlers
  const handleOrgsOpen = useCallback(() => {
    if (orgsOptions.length === 0)
      fetchOrgs({ page: 1, search: "", append: false });
  }, [orgsOptions.length, fetchOrgs]);

  const handleOrgsScrollBottom = useCallback(() => {
    if (!orgsHasMore || orgsLoading) return;
    fetchOrgs({ page: orgsPage + 1, search: orgsSearch, append: true });
  }, [orgsHasMore, orgsLoading, orgsPage, orgsSearch, fetchOrgs]);

  const handleOrgsSearch = useCallback((val) => {
    setOrgsSearch(val);
    setOrgsPage(1);
    setOrgsHasMore(true);
    fetchOrgs({ page: 1, search: val, append: false });
  }, [fetchOrgs]);

  const handleOrgsRefresh = useCallback(() => {
    setOrgsSearch("");
    setOrgsPage(1);
    setOrgsHasMore(true);
    fetchOrgs({ page: 1, search: "", append: false });
  }, [fetchOrgs]);

  // Departments Handlers
  const handleDeptsOpen = useCallback(() => {
    if (deptsOptions.length === 0)
      fetchDepts({ page: 1, search: "", append: false });
  }, [deptsOptions.length, fetchDepts]);

  const handleDeptsScrollBottom = useCallback(() => {
    if (!deptsHasMore || deptsLoading) return;
    fetchDepts({ page: deptsPage + 1, search: deptsSearch, append: true });
  }, [deptsHasMore, deptsLoading, deptsPage, deptsSearch, fetchDepts]);

  const handleDeptsSearch = useCallback((val) => {
    setDeptsSearch(val);
    setDeptsPage(1);
    setDeptsHasMore(true);
    fetchDepts({ page: 1, search: val, append: false });
  }, [fetchDepts]);

  const handleDeptsRefresh = useCallback(() => {
    setDeptsSearch("");
    setDeptsPage(1);
    setDeptsHasMore(true);
    fetchDepts({ page: 1, search: "", append: false });
  }, [fetchDepts]);

  // Courses Handlers
  const handleCoursesOpen = useCallback(() => {
    if (menuOpenInProgress.current) return;
    menuOpenInProgress.current = true;
    
    setCoursesOptions([]);
    setCoursesPage(1);
    setCoursesHasMore(true);
    setCoursesSearch("");
    fetchCourses({ page: 1, search: "", append: false }).finally(() => {
      menuOpenInProgress.current = false;
    });
  }, [fetchCourses]);

  const handleCoursesScrollBottom = useCallback(() => {
    if (!coursesHasMore || coursesLoading) return;
    fetchCourses({
      page: coursesPage + 1,
      search: coursesSearch,
      append: true,
    });
  }, [coursesHasMore, coursesLoading, coursesPage, coursesSearch, fetchCourses]);

  const handleCoursesSearch = useCallback((val) => {
    setCoursesSearch(val);
    setCoursesPage(1);
    setCoursesHasMore(true);
    fetchCourses({ page: 1, search: val, append: false });
  }, [fetchCourses]);

  const handleCoursesRefresh = useCallback(() => {
    setCoursesSearch("");
    setCoursesPage(1);
    setCoursesHasMore(true);
    fetchCourses({ page: 1, search: "", append: false });
  }, [fetchCourses]);

  // Batches Handlers
  const handleBatchesOpen = useCallback(() => {
    if (menuOpenInProgress.current) return;
    menuOpenInProgress.current = true;
    
    setBatchesOptions([]);
    setBatchesPage(1);
    setBatchesHasMore(true);
    setBatchesSearch("");
    fetchBatches({ page: 1, search: "", append: false }).finally(() => {
      menuOpenInProgress.current = false;
    });
  }, [fetchBatches]);

  const handleBatchesScrollBottom = useCallback(() => {
    if (!batchesHasMore || batchesLoading) return;
    fetchBatches({
      page: batchesPage + 1,
      search: batchesSearch,
      append: true,
    });
  }, [batchesHasMore, batchesLoading, batchesPage, batchesSearch, fetchBatches]);

  const handleBatchesSearch = useCallback((val) => {
    setBatchesSearch(val);
    setBatchesPage(1);
    setBatchesHasMore(true);
    fetchBatches({ page: 1, search: val, append: false });
  }, [fetchBatches]);

  const handleBatchesRefresh = useCallback(() => {
    setBatchesSearch("");
    setBatchesPage(1);
    setBatchesHasMore(true);
    fetchBatches({ page: 1, search: "", append: false });
  }, [fetchBatches]);

  // Coursewise Students Handlers
  const handleCwStudentsOpen = useCallback((selectedCourses) => {
    if (!selectedCourses?.length) return;
    const courseIds = selectedCourses.map((c) => c.value);
    if (cwStudentsOptions.length === 0) {
      fetchCwStudents({ courseIds });
    }
  }, [cwStudentsOptions.length, fetchCwStudents]);

  const handleCwStudentsScrollBottom = useCallback((selectedCourses) => {
    // No-op: Pagination removed
  }, []);

  const handleCwStudentsRefresh = useCallback((selectedCourses) => {
    const courseIds = (selectedCourses || []).map((c) => c.value);
    setCwStudentsOptions([]);
    fetchCwStudents({ courseIds });
  }, [fetchCwStudents]);

  // Batchwise Students Handlers
  const handleBwStudentsOpen = useCallback((selectedBatches) => {
    if (!selectedBatches?.length) return;
    const batchIds = selectedBatches.map((b) => b.value);
    if (bwStudentsOptions.length === 0) {
      fetchBwStudents({ batchIds });
    }
  }, [bwStudentsOptions.length, fetchBwStudents]);

  const handleBwStudentsScrollBottom = useCallback((selectedBatches) => {
    // No-op: Pagination removed
  }, []);

  const handleBwStudentsRefresh = useCallback((selectedBatches) => {
    const batchIds = (selectedBatches || []).map((b) => b.value);
    setBwStudentsOptions([]);
    fetchBwStudents({ batchIds });
  }, [fetchBwStudents]);

  // ══════════════════════════════════════════════════════════════════════════
  //  FIELD HANDLERS
  // ══════════════════════════════════════════════════════════════════════════

  const handleOrganizationSelection = useCallback((selected, setFieldValue) => {
    setFieldValue(
      "organizationId",
      selected?.organizationId || selected?.value || selected?._id || "",
    );
    setFieldValue(
      "organizationName",
      selected?.organizationName || selected?.label || "",
    );
  }, []);

  const handleContactSelection = useCallback((selected, setFieldValue) => {
    setFieldValue("contactId", selected?.value || "");
    if (selected) {
      if (selected.organizationId) {
        setFieldValue("organizationId", selected.organizationId);
      }
      if (selected.organizationName) {
        setFieldValue("organizationName", selected.organizationName);
      }
    } else {
      setFieldValue("organizationId", "");
      setFieldValue("organizationName", "");
    }
  }, []);

  // FIXED: Only clear student selections when courses are REMOVED
  const handleCourseSelection = useCallback((selected, setFieldValue, currentSelectedCourses) => {
    // Check if courses were removed (not just added)
    const previousCourseIds = currentSelectedCourses.map(c => c.value);
    const newCourseIds = (selected || []).map(c => c.value);
    const removedCourses = previousCourseIds.filter(id => !newCourseIds.includes(id));
    
    // Only clear coursewise students if courses were REMOVED
    if (removedCourses.length > 0) {
      setFieldValue("coursewiseStudents", []);
      setCwStudentsOptions([]);
    }
    
    setFieldValue("selectedCourses", selected || []);

    // Automatically fetch students for the selected courses
    if (newCourseIds.length > 0) {
      fetchCwStudents({ courseIds: newCourseIds });
    } else {
      setCwStudentsOptions([]);
    }
  }, [fetchCwStudents]);

  // FIXED: Only clear student selections when batches are REMOVED
  const handleBatchSelection = useCallback((selected, setFieldValue, currentSelectedBatches) => {
    // Check if batches were removed (not just added)
    const previousBatchIds = currentSelectedBatches.map(b => b.value);
    const newBatchIds = (selected || []).map(b => b.value);
    const removedBatches = previousBatchIds.filter(id => !newBatchIds.includes(id));
    
    // Only clear batchwise students if batches were REMOVED
    if (removedBatches.length > 0) {
      setFieldValue("batchwiseStudents", []);
      setBwStudentsOptions([]);
    }
    
    setFieldValue("selectedBatches", selected || []);

    // Automatically fetch students for the selected batches
    if (newBatchIds.length > 0) {
      fetchBwStudents({ batchIds: newBatchIds });
    } else {
      setBwStudentsOptions([]);
    }
  }, [fetchBwStudents]);

  // ══════════════════════════════════════════════════════════════════════════
  //  ACCORDION
  // ══════════════════════════════════════════════════════════════════════════

  const handleAccordionToggle = useCallback((section) => {
    setExpandedAccordion((prev) => {
      const reset = {
        jobDetails: false,
        studentSelectionDetails: false,
        jobDescriptionDetails: false,
      };
      return { ...reset, [section]: !prev[section] };
    });
  }, []);

  // ══════════════════════════════════════════════════════════════════════════
  //  SUBMIT - Fixed to prevent double submission and handle onHide correctly
  // ══════════════════════════════════════════════════════════════════════════

  const handleSubmit = useCallback(async (values, { setSubmitting }) => {
    // Prevent multiple submissions
    if (isSubmitting.current) {
      // console.log("Submission already in progress, skipping...");
      return;
    }
    
    // Check if placement.id exists
    if (!placement?.id) {
      toast.error("Placement ID is missing. Please refresh and try again.", {
        position: "top-right",
        autoClose: 5000,
      });
      if (setSubmitting) setSubmitting(false);
      return;
    }
    
    try {
      isSubmitting.current = true;
      setIsLoading(true);

      const courseIds = values.selectedCourses.map((c) => c.value);
      const batchIds = values.selectedBatches.map((b) => b.value);
      const coursewiseStudentIds = values.coursewiseStudents.map(
        (s) => s.value,
      );
      const batchwiseStudentIds = values.batchwiseStudents.map((s) => s.value);

      const formData = {
        jobName: String(values.jobName || ""),
        organizationName: String(values.organizationName || ""),
        organizationId: String(values.organizationId || ""),
        jobPostedDate: String(values.jobPostedDate || ""),
        salary: String(values.salary || ""),
        package: Number(values.salary || 0),
        jobLocation: String(values.jobLocation || ""),
        status: String(values.status || ""),
        assignedToId: String(values.assignedToId || ""),
        department: String(values.department || ""),
        contactId: String(values.contactId || ""),
        jobPositionStatus: String(values.jobPositionStatus || ""),
        batchIds: String(batchIds.join(",") || ""),
        courseModuleIds: String(courseIds.join(",") || ""),
        coursewiseStudentIds: String(coursewiseStudentIds.join(",") || ""),
        batchwiseStudentIds: String(batchwiseStudentIds.join(",") || ""),
        jobDescription: String(values.jobDescription || ""),
        updatedAt: new Date().toISOString(),
      };

      const result = await updatePlacement(placement.id, formData);

      toast.success(`Job "${values.jobName}" updated successfully!`, {
        position: "top-right",
        autoClose: 3000,
        style: {
          backgroundColor: "#d4edda",
          color: "#155724",
          border: "1px solid #c3e6cb",
        },
      });

      // First call onUpdatePlacement if provided
      if (onUpdatePlacement) {
        onUpdatePlacement(result);
      }
      
      // Then call onHide after a small delay to ensure toast is seen
      // and to prevent any race conditions
      setTimeout(() => {
        if (onHide && isComponentMounted.current) {
          onHide();
        }
      }, 100);
      
    } catch (error) {
      console.error("Form submission error:", error);
      toast.error(`Failed to update job: ${error.message}`, {
        position: "top-right",
        autoClose: 5000,
        style: {
          backgroundColor: "#f8d7da",
          color: "#721c24",
          border: "1px solid #f5c6cb",
        },
      });
    } finally {
      setIsLoading(false);
      isSubmitting.current = false;
      if (setSubmitting) setSubmitting(false);
    }
  }, [placement?.id, onUpdatePlacement, onHide]);

  // ══════════════════════════════════════════════════════════════════════════
  //  RENDER
  // ══════════════════════════════════════════════════════════════════════════

  if (!placement) return null;

  return (
    <>
      <Formik
        initialValues={getInitialValues()}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        enableReinitialize={false}
        validateOnMount={true}
      >
        {({ values, setFieldValue, isSubmitting: isFormikSubmitting }) => (
          <FormikForm>
            <ScrollToFieldError setExpandedAccordion={setExpandedAccordion} />

            {/* ── Job & Assignment Details ───────────────────────────────── */}
            <Card className="border-0 shadow-sm mb-3">
              <Card.Header
                className="bg-transparent border-0 px-3 py-3"
                onClick={() => handleAccordionToggle("jobDetails")}
                style={{ cursor: "pointer", backgroundColor: "#fff7ed" }}
              >
                <div className="d-flex align-items-center">
                  <ChevronDown
                    size={20}
                    className="me-2"
                    style={{
                      color: "#ea580c",
                      transform: expandedAccordion.jobDetails
                        ? "rotate(0deg)"
                        : "rotate(-90deg)",
                      transition: "transform 0.3s ease",
                    }}
                  />
                  <Briefcase
                    className="me-2"
                    size={18}
                    style={{ color: "#ea580c" }}
                  />
                  <h5 className="mb-0 fw-normal text-dark">
                    Job & Assignment Details
                  </h5>
                </div>
              </Card.Header>

              {expandedAccordion.jobDetails && (
                <Card.Body className="px-3 py-4">
                  <h6 className="text-muted mb-3 fw-bold">
                    Assignment Information
                  </h6>
                  <Row className="g-4">
                    {/* Assigned To */}
                    <Col md={6}>
                      <label className="form-label text-dark fw-bold">
                        * Assigned To
                      </label>
                      <div data-field="assignedToId">
                        <CustomAsyncDropdown
                          isMulti={false}
                          options={placementOfficersOptions}
                          value={resolveOption(
                            placementOfficersOptions,
                            values.assignedToId,
                            () =>
                              fallbackPlacementOfficerOption(
                                values.assignedToId,
                              ),
                          )}
                          onChange={(selected) =>
                            setFieldValue("assignedToId", selected?.value || "")
                          }
                          placeholder="Search and select placement officer"
                          isLoading={placementOfficersLoading}
                          onMenuOpen={handlePlacementOfficersOpen}
                          onInputChange={handlePlacementOfficersSearch}
                          onMenuScrollToBottom={
                            handlePlacementOfficersScrollBottom
                          }
                          onRefresh={handlePlacementOfficersRefresh}
                          isDisabled={false}
                        />
                      </div>
                      <ErrorMessage
                        name="assignedToId"
                        component="div"
                        className="text-danger small mt-1"
                      />
                    </Col>

                    {/* HR Contact */}
                    <Col md={6}>
                      <label className="form-label text-dark fw-bold">
                        * HR Contact
                      </label>
                      <div data-field="contactId">
                        <CustomAsyncDropdown
                          isMulti={false}
                          options={contactsOptions}
                          value={resolveOption(
                            contactsOptions,
                            values.contactId,
                            () => fallbackContactOption(values.contactId),
                          )}
                          onChange={(selected) =>
                            handleContactSelection(selected, setFieldValue)
                          }
                          placeholder="Search and select contact"
                          isLoading={contactsLoading}
                          onMenuOpen={handleContactsOpen}
                          onInputChange={handleContactsSearch}
                          onMenuScrollToBottom={handleContactsScrollBottom}
                          onRefresh={handleContactsRefresh}
                          isDisabled={false}
                        />
                      </div>
                      <ErrorMessage
                        name="contactId"
                        component="div"
                        className="text-danger small mt-1"
                      />
                    </Col>
                  </Row>

                  <div className="mt-4 pt-4 border-top">
                    <h6 className="text-muted mb-3 fw-bold">Job Information</h6>
                    <Row className="g-4">
                      {/* Job Name */}
                      <Col md={6}>
                        <TextInput
                          label="Job Name"
                          name="jobName"
                          placeholder="Enter job name"
                        />
                      </Col>

                      {/* Organization */}
                      <Col md={6}>
                        <label className="form-label text-dark fw-bold">
                          * Organization
                        </label>
                        <div data-field="organization">
                          <CustomAsyncDropdown
                            isMulti={false}
                            options={orgsOptions}
                            value={resolveOption(
                              orgsOptions,
                              values.organizationId,
                              () =>
                                fallbackOrgOption(
                                  values.organizationId,
                                  values.organizationName,
                                ),
                            )}
                            onChange={(selected) =>
                              handleOrganizationSelection(
                                selected,
                                setFieldValue,
                              )
                            }
                            placeholder="Search and select organization"
                            isLoading={orgsLoading}
                            onMenuOpen={handleOrgsOpen}
                            onInputChange={handleOrgsSearch}
                            onMenuScrollToBottom={handleOrgsScrollBottom}
                            onRefresh={handleOrgsRefresh}
                            isDisabled={false}
                          />
                        </div>
                        <ErrorMessage
                          name="organizationName"
                          component="div"
                          className="text-danger small mt-1"
                        />
                      </Col>
                    </Row>

                    <Row className="g-4 mt-2">
                      {/* Department */}
                      <Col md={6}>
                        <label className="form-label text-dark fw-bold">
                          * Department
                        </label>
                        <div data-field="department">
                          <CustomAsyncDropdown
                            isMulti={false}
                            options={deptsOptions}
                            value={resolveOption(
                              deptsOptions,
                              values.department,
                              () => fallbackDeptOption(values.department),
                            )}
                            onChange={(selected) =>
                              setFieldValue("department", selected?.value || "")
                            }
                            placeholder="Search and select department"
                            isLoading={deptsLoading}
                            onMenuOpen={handleDeptsOpen}
                            onInputChange={handleDeptsSearch}
                            onMenuScrollToBottom={handleDeptsScrollBottom}
                            onRefresh={handleDeptsRefresh}
                            isDisabled={false}
                          />
                        </div>
                        <ErrorMessage
                          name="department"
                          component="div"
                          className="text-danger small mt-1"
                        />
                      </Col>

                      {/* Job Posted Date */}
                      <Col md={6}>
                        <label className="form-label text-dark fw-bold">
                          * Job Posted Date
                        </label>
                        <input
                          type="date"
                          name="jobPostedDate"
                          className="form-control"
                          value={values.jobPostedDate}
                          onChange={(e) =>
                            setFieldValue("jobPostedDate", e.target.value)
                          }
                          style={{
                            border: "1px solid #ced4da",
                            borderRadius: "0.375rem",
                            minHeight: "38px",
                          }}
                        />
                        <ErrorMessage
                          name="jobPostedDate"
                          component="div"
                          className="text-danger small mt-1"
                        />
                      </Col>
                    </Row>

                    <Row className="g-4 mt-2">
                      <Col md={6}>
                        <TextInput
                          label="Salary"
                          name="salary"
                          placeholder="Enter salary range"
                        />
                      </Col>
                      <Col md={6}>
                        <TextInput
                          label="Job Location"
                          name="jobLocation"
                          placeholder="Enter job location"
                        />
                      </Col>
                    </Row>

                    <Row className="g-4 mt-2">
                      {/* Status */}
                      <Col md={6}>
                        <label className="form-label text-dark fw-bold">
                          * Status
                        </label>
                        <div data-field="status">
                          <CustomAsyncDropdown
                            isMulti={false}
                            options={hardcodedOptions.status}
                            value={
                              hardcodedOptions.status.find(
                                (o) => o.value === values.status,
                              ) || null
                            }
                            onChange={(selected) =>
                              setFieldValue("status", selected?.value || "")
                            }
                            placeholder="Select status"
                            isLoading={false}
                            isDisabled={false}
                            onMenuOpen={() => {}}
                          />
                        </div>
                        <ErrorMessage
                          name="status"
                          component="div"
                          className="text-danger small mt-1"
                        />
                      </Col>

                      {/* Job Position Status */}
                      <Col md={6}>
                        <label className="form-label text-dark fw-bold">
                          * Job Position Status
                        </label>
                        <div data-field="jobPositionStatus">
                          <CustomAsyncDropdown
                            isMulti={false}
                            options={hardcodedOptions.jobPositionStatus}
                            value={
                              hardcodedOptions.jobPositionStatus.find(
                                (o) => o.value === values.jobPositionStatus,
                              ) || null
                            }
                            onChange={(selected) =>
                              setFieldValue(
                                "jobPositionStatus",
                                selected?.value || "",
                              )
                            }
                            placeholder="Select job position status"
                            isLoading={false}
                            isDisabled={false}
                            onMenuOpen={() => {}}
                          />
                        </div>
                        <ErrorMessage
                          name="jobPositionStatus"
                          component="div"
                          className="text-danger small mt-1"
                        />
                      </Col>
                    </Row>
                  </div>
                </Card.Body>
              )}
            </Card>

            {/* ── Student Selection ──────────────────────────────────────── */}
            <Card className="border-0 shadow-sm mb-3">
              <Card.Header
                className="bg-transparent border-0 px-3 py-3"
                onClick={() => handleAccordionToggle("studentSelectionDetails")}
                style={{ cursor: "pointer", backgroundColor: "#fff7ed" }}
              >
                <div className="d-flex align-items-center">
                  <ChevronDown
                    size={20}
                    className="me-2"
                    style={{
                      color: "#ea580c",
                      transform: expandedAccordion.studentSelectionDetails
                        ? "rotate(0deg)"
                        : "rotate(-90deg)",
                      transition: "transform 0.3s ease",
                    }}
                  />
                  <Users
                    className="me-2"
                    size={18}
                    style={{ color: "#ea580c" }}
                  />
                  <h5 className="mb-0 fw-normal text-dark">
                    Student Selection
                  </h5>
                </div>
              </Card.Header>

              {expandedAccordion.studentSelectionDetails && (
                <Card.Body className="px-3 py-4">
                  <Row className="g-4">
                    {/* Courses */}
                    <Col md={6}>
                      <label className="form-label text-dark fw-bold">
                        Select Courses (Multi-select)
                      </label>
                      <div data-field="selectedCourses">
                        <CustomAsyncDropdown
                          isMulti={true}
                          options={coursesOptions}
                          value={values.selectedCourses}
                          onChange={(selected) =>
                            handleCourseSelection(selected, setFieldValue, values.selectedCourses)
                          }
                          placeholder="Search and select multiple courses"
                          isLoading={coursesLoading}
                          onMenuOpen={handleCoursesOpen}
                          onInputChange={handleCoursesSearch}
                          onMenuScrollToBottom={handleCoursesScrollBottom}
                          onRefresh={handleCoursesRefresh}
                          isDisabled={false}
                        />
                      </div>
                    </Col>

                    {/* Coursewise Students */}
                    <Col md={6}>
                      <label className="form-label text-dark fw-bold">
                        Select Coursewise Students
                      </label>
                      <div data-field="coursewiseStudents">
                        <CustomAsyncDropdown
                          isMulti={true}
                          options={cwStudentsOptions}
                          value={values.coursewiseStudents}
                          onChange={(selected) =>
                            setFieldValue("coursewiseStudents", selected || [])
                          }
                          placeholder={
                            values.selectedCourses.length > 0
                              ? "Select students from chosen courses"
                              : "First select courses to see students"
                          }
                          isLoading={cwStudentsLoading}
                          isDisabled={values.selectedCourses.length === 0}
                          onMenuOpen={() =>
                            handleCwStudentsOpen(values.selectedCourses)
                          }
                          onMenuScrollToBottom={() =>
                            handleCwStudentsScrollBottom(values.selectedCourses)
                          }
                          onRefresh={() =>
                            handleCwStudentsRefresh(values.selectedCourses)
                          }
                          showSelectAll={true}
                        />
                      </div>
                    </Col>
                  </Row>

                  <Row className="g-4 mt-2">
                    {/* Batches */}
                    <Col md={6}>
                      <label className="form-label text-dark fw-bold">
                        Select Batches (Multi-select)
                      </label>
                      <div data-field="selectedBatches">
                        <CustomAsyncDropdown
                          isMulti={true}
                          options={batchesOptions}
                          value={values.selectedBatches}
                          onChange={(selected) =>
                            handleBatchSelection(selected, setFieldValue, values.selectedBatches)
                          }
                          placeholder="Select multiple batches"
                          isLoading={batchesLoading}
                          onMenuOpen={handleBatchesOpen}
                          onInputChange={handleBatchesSearch}
                          onMenuScrollToBottom={handleBatchesScrollBottom}
                          onRefresh={handleBatchesRefresh}
                          isDisabled={false}
                        />
                      </div>
                    </Col>

                    {/* Batchwise Students */}
                    <Col md={6}>
                      <label className="form-label text-dark fw-bold">
                        Select Batchwise Students
                      </label>
                      <div data-field="batchwiseStudents">
                        <CustomAsyncDropdown
                          isMulti={true}
                          options={bwStudentsOptions}
                          value={values.batchwiseStudents}
                          onChange={(selected) =>
                            setFieldValue("batchwiseStudents", selected || [])
                          }
                          placeholder={
                            values.selectedBatches.length > 0
                              ? "Select students from chosen batches"
                              : "First select batches to see students"
                          }
                          isLoading={bwStudentsLoading}
                          isDisabled={values.selectedBatches.length === 0}
                          onMenuOpen={() =>
                            handleBwStudentsOpen(values.selectedBatches)
                          }
                          onMenuScrollToBottom={() =>
                            handleBwStudentsScrollBottom(values.selectedBatches)
                          }
                          onRefresh={() =>
                            handleBwStudentsRefresh(values.selectedBatches)
                          }
                          showSelectAll={true}
                        />
                      </div>
                    </Col>
                  </Row>
                </Card.Body>
              )}
            </Card>

            {/* ── Job Description ────────────────────────────────────────── */}
            <Card className="border-0 shadow-sm mb-3">
              <Card.Header
                className="bg-transparent border-0 px-3 py-3"
                onClick={() => handleAccordionToggle("jobDescriptionDetails")}
                style={{ cursor: "pointer", backgroundColor: "#fff7ed" }}
              >
                <div className="d-flex align-items-center">
                  <ChevronDown
                    size={20}
                    className="me-2"
                    style={{
                      color: "#ea580c",
                      transform: expandedAccordion.jobDescriptionDetails
                        ? "rotate(0deg)"
                        : "rotate(-90deg)",
                      transition: "transform 0.3s ease",
                    }}
                  />
                  <FileText
                    className="me-2"
                    size={18}
                    style={{ color: "#ea580c" }}
                  />
                  <h5 className="mb-0 fw-normal text-dark">Job Description</h5>
                </div>
              </Card.Header>

              {expandedAccordion.jobDescriptionDetails && (
                <Card.Body className="px-3 py-4">
                  <div
                    className="ck-editor-container"
                    style={{
                      border: "1px solid #ced4da",
                      borderRadius: "0.375rem",
                      minHeight: "300px",
                    }}
                  >
                    <CKEditor
                      editor={ClassicEditor}
                      data={values.jobDescription || ""}
                      onReady={(editor) => {
                        const toolbar = editor.ui.view.toolbar;
                        if (toolbar?.element) {
                          toolbar.element.style.display = "flex";
                          toolbar.element.style.visibility = "visible";
                          toolbar.element.style.opacity = "1";
                        }
                        editor.editing.view.focus();
                      }}
                      onChange={(event, editor) =>
                        setFieldValue("jobDescription", editor.getData())
                      }
                      config={{
                        licenseKey: "",
                        toolbar: {
                          items: [
                            "heading",
                            "|",
                            "bold",
                            "italic",
                            "underline",
                            "strikethrough",
                            "|",
                            "bulletedList",
                            "numberedList",
                            "|",
                            "outdent",
                            "indent",
                            "|",
                            "link",
                            "|",
                            "insertTable",
                            "|",
                            "undo",
                            "redo",
                          ],
                          shouldNotGroupWhenFull: false,
                        },
                        height: 300,
                        placeholder: "Enter the job description here...",
                        removePlugins: ["MediaEmbed"],
                      }}
                    />
                  </div>
                </Card.Body>
              )}
            </Card>

            {/* ── Action Buttons ─────────────────────────────────────────── */}
            <div
              className="d-flex justify-content-end gap-2 mt-3 pt-3 border-top"
              style={{
                backgroundColor: "#fff7ed",
                padding: "12px 16px",
                borderRadius: "0 0 8px 8px",
              }}
            >
              <Button
                variant="outline-secondary"
                onClick={() => {
                  if (!isLoading && !isSubmitting.current && onHide) {
                    onHide();
                  }
                }}
                disabled={isLoading || isSubmitting.current}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="px-4"
                disabled={isLoading || isSubmitting.current || isFormikSubmitting}
                style={{
                  backgroundColor: "#ea580c",
                  borderColor: "#ea580c",
                  color: "white",
                }}
              >
                {isLoading ? (
                  <>
                    <Spinner
                      animation="border"
                      size="sm"
                      className="me-2"
                      role="status"
                    />
                    Updating...
                  </>
                ) : (
                  "Update Job Profile"
                )}
              </Button>
            </div>
          </FormikForm>
        )}
      </Formik>

      <ToastContainer />

      <style>{`
        .ck-editor__editable { min-height: 250px; }
        .invalid-feedback { display: block; }
      `}</style>
    </>
  );
};

export default PlacementDetailsTab;